import React, { useEffect, useState } from "react";
import { Mic, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { VoiceEditor } from "@/components/VoiceEditor";
import { VoiceCompare } from "@/components/VoiceCompare";
import { StyleEditor } from "@/components/StyleEditor";
import { VoiceHealth } from "@/components/VoiceHealth";
import { VoiceTrainingPanel } from "@/components/VoiceTrainingPanel";

const BLANK = {
  name: "", preset: "Noah", gender: "male", persona: "", enabled: true,
  settings: { stability: 0.75, similarity_boost: 0.9, style: 0.15, speed: 1.1 },
  fx: { pitch: 0, depth: 0, clarity: 0, echo: 0, reverb: 0, radio: false, volume: 1 },
};

const VoiceRow = ({ v, onEdit, onToggle, onDelete }) => (
  <div className="border border-border rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`voice-row-${v.id}`}>
    <div className="min-w-0">
      <div className="font-bold">{v.name}</div>
      <div className="text-xs text-muted-foreground">{v.gender === "female" ? "امرأة" : "رجل"} · {v.trained ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold">مدرّب — يظهر للعملاء</span> : v.visible_to_customers ? <span className="text-sky-600 dark:text-sky-400 font-semibold" data-testid={`voice-preset-${v.id}`}>صوت أساسي جاهز — يظهر للعملاء (درّبه بتسجيلك ليصبح مميزًا)</span> : <span className="text-amber-600 dark:text-amber-400 font-semibold" data-testid={`voice-untrained-${v.id}`}>{v.enabled ? "مخفي عن العملاء — مستنسخ بلا تدريب أو فشل في آخر فحص" : "موقوف — مخفي عن العملاء"}</span>}</div>
      {v.last_check && (
        <div className={`text-[11px] mt-0.5 ${v.last_check.ok ? "text-emerald-600 dark:text-emerald-400" : "text-destructive font-bold"}`} data-testid={`voice-status-${v.id}`}>
          {v.last_check.ok ? "✓ يعمل بصوته المحفوظ" : `✗ ${v.last_check.error}`}
        </div>
      )}
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <Switch checked={!!v.enabled} onCheckedChange={(c) => onToggle(v, c)} data-testid={`voice-enabled-${v.id}`} />
      <Button size="icon" variant="outline" className="border-border" onClick={() => onEdit(v)} data-testid={`voice-edit-${v.id}`}><Pencil className="w-4 h-4" /></Button>
      <Button size="icon" variant="outline" className="border-border text-destructive" onClick={() => onDelete(v)} data-testid={`voice-delete-${v.id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  </div>
);

export const VoiceLab = () => {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = async () => {
    try { const r = await api.get("/admin/voices"); setData(r.data); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const save = async (v) => {
    const { id, created_at, _id, clone_id, ref_id, ref_name, trained_at, ...body } = v;
    try {
      if (id) await api.put(`/admin/voices/${id}`, body); else await api.post("/admin/voices", body);
      toast.success("تم حفظ الصوت واعتماده"); setEdit(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const remove = async (v) => {
    if (!window.confirm(`حذف صوت ${v.name}؟`)) return;
    try { await api.delete(`/admin/voices/${v.id}`); toast.success("تم الحذف"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  return (
    <div className="glass rounded-2xl p-5 border border-border space-y-4" data-testid="voice-lab-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-bold flex items-center gap-2"><Mic className="w-4 h-4 text-primary" /> استوديو الأصوات والشخصيات</div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">درّب شخصيات صوتية سعودية للإعلانات: أضف شخصية، ارفع أو سجّل صوتها، اضبطها وجرّبها، ثم اعتمدها لتظهر للعملاء عند توليد الفيديو.</p>
        </div>
        <Button onClick={() => setEdit({ ...BLANK })} className="gap-2 shrink-0" data-testid="voice-add-btn"><Plus className="w-4 h-4" /> صوت جديد</Button>
      </div>
      {!data ? <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.voices.map((v) => <VoiceRow key={v.id} v={v} onEdit={setEdit} onDelete={remove} onToggle={(x, c) => save({ ...x, enabled: c })} />)}
        </div>
      )}
      {data && <VoiceTrainingPanel voices={data.voices} onChanged={load} />}
      {data && <VoiceHealth onDone={load} />}
      {data && (
        <div className="flex items-center justify-between gap-3 flex-wrap border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">عينات العملاء تُولّد مرة وحدة لكل شخصية وأسلوب وتُحفظ، فما تستهلك رصيد مع كل استماع. تتحدث تلقائياً عند تعديل أي شخصية أو أسلوب.</p>
          <Button variant="outline" className="border-border" onClick={async () => { try { const r = await api.post("/admin/voices/samples"); toast.success(`بدأ تجهيز ${r.data.started} عينة`); } catch (err) { toast.error(formatError(err.response?.data?.detail)); } }} data-testid="build-samples-btn">تجهيز عينات العملاء</Button>
        </div>
      )}
      {data && <StyleEditor styles={data.styles} voices={data.voices.filter((v) => v.enabled)} sampleText={data.sample_text} onSaved={load} />}
      {data && <VoiceCompare voices={data.voices.filter((v) => v.enabled)} sampleText={data.sample_text} />}
      {edit && data && <VoiceEditor voice={edit} presets={data.presets} sampleText={data.sample_text} onSave={save} onClose={() => setEdit(null)} />}
    </div>
  );
};
